// ============================================
// LIGA AJUPAM - App Settings Service
// ============================================
// Gestión de configuración global de la aplicación.
// v6.5.0 Fase 3: lectura vía RPC `get_app_settings_v2(p_league_id)`.
// v6.5.0 Fase 8b: escrituras se realizan vía updateSettings() en ./admin
// (RPC `admin_update_settings(..., p_league_id)`). Helpers legacy removidos.
// @ts-nocheck - Supabase types use simplified `any` for flexibility

import { supabase, handleSupabaseError } from './client';
import { withTimeout } from '../../utils/withTimeout';
import type { AppSettings } from '../../types';

/**
 * Obtener configuración de una liga (o la liga actual si no se especifica).
 *
 * @param leagueId - UUID de la liga. Si se omite, el backend resuelve la liga
 *                   `is_current=true` (compatibilidad Fase 2 dual-read).
 */
export const getAppSettings = async (leagueId?: string | null): Promise<AppSettings> => {
  try {
    const { data, error } = await withTimeout(
      (supabase.rpc as any)('get_app_settings_v2', { p_league_id: leagueId ?? null }),
      15000,
      'get_app_settings_v2',
    );

    if (error) throw handleSupabaseError(error);

    const row = Array.isArray(data) ? data[0] : data;
    if (!row) throw new Error('App settings not found');

    return row as AppSettings;
  } catch (error: any) {
    console.error('Error fetching app settings:', error);
    throw error;
  }
};

// v6.5.0 Fase 8b — helpers legacy (updateAppSettings/closeRegistration/openRegistration/
// startSeason/endSeason) removidos. Sin callers en la app. Las escrituras admin se
// realizan vía updateSettings() de ./admin (RPC admin_update_settings) respetando
// p_league_id y pasando por league_settings.

// ============================================
// v6.9.2 Fase G — CTA galería profesional
// ============================================

export interface ProGalleryUpdateInput {
  url?: string | null;
  label?: string | null;
  cover_url?: string | null;
  /** Si true, despublica (set NULL en url + cover). */
  clear?: boolean;
}

/**
 * Configurar CTA a la galería profesional externa por liga.
 * Devuelve la fila completa de league_settings actualizada.
 */
export const adminUpdateProGallery = async (
  adminSessionToken: string,
  input: ProGalleryUpdateInput,
  leagueId?: string | null
): Promise<AppSettings> => {
  const { data, error } = await (supabase.rpc as any)('admin_update_pro_gallery', {
    p_admin_session_token: adminSessionToken,
    p_url: input.url ?? null,
    p_label: input.label ?? null,
    p_cover_url: input.cover_url ?? null,
    p_league_id: leagueId ?? null,
    p_clear: input.clear ?? false,
  });
  if (error) throw handleSupabaseError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No se pudo actualizar la galería profesional');
  return row as AppSettings;
};

/**
 * v6.11.256 — Crédito del fotógrafo profesional (nombre e Instagram) que usan las
 * historias automáticas («Ya están las fotos del evento» y el cierre de temporada).
 * Vacío = sin crédito. Devuelve la fila completa de league_settings actualizada.
 */
export const adminUpdateProGalleryCredit = async (
  adminSessionToken: string,
  input: { credit: string | null; instagram: string | null },
  leagueId?: string | null
): Promise<AppSettings> => {
  const { data, error } = await (supabase.rpc as any)('admin_update_pro_gallery_credit', {
    p_admin_session_token: adminSessionToken,
    p_credit: input.credit,
    p_instagram: input.instagram,
    p_league_id: leagueId ?? null,
  });
  if (error) throw handleSupabaseError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No se pudo guardar el crédito del fotógrafo');
  return row as AppSettings;
};

// ============================================
// v6.11.210 — Placa «JUGADOR/A CONFIRMADO/A»
// ============================================

/**
 * Prende o apaga, para UNA liga, la placa de jugador/a confirmado/a que se les
 * ofrece a los inscriptos mientras la inscripción está abierta.
 *
 * La liga es obligatoria en la práctica: con `null` la RPC rechaza ("Falta
 * indicar sobre qué liga se opera") en vez de adivinar una. Devuelve la fila
 * completa de league_settings ya actualizada.
 */
export const adminSetPlayerCardEnabled = async (
  adminSessionToken: string,
  enabled: boolean,
  leagueId: string | null
): Promise<AppSettings> => {
  const { data, error } = await (supabase.rpc as any)('admin_set_player_card_enabled', {
    p_admin_session_token: adminSessionToken,
    p_enabled: enabled,
    p_league_id: leagueId,
  });
  if (error) throw handleSupabaseError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No se pudo guardar la configuración de la placa');
  return row as AppSettings;
};

// ============================================
// v6.9.2 Fase J — CTA "Evento de finales"
// ============================================

export interface FinalsEventUpdateInput {
  enabled?: boolean;
  title?: string | null;
  subtitle?: string | null;
  description?: string | null;
  dates_text?: string | null;
  cover_url?: string | null;
  extra_images?: string[] | null;
  venue_url?: string | null;
  venue_label?: string | null;
}

export const adminUpdateFinalsEvent = async (
  adminSessionToken: string,
  input: FinalsEventUpdateInput,
  leagueId?: string | null
): Promise<AppSettings> => {
  const { data, error } = await (supabase.rpc as any)('admin_update_finals_event', {
    p_admin_session_token: adminSessionToken,
    p_enabled: input.enabled ?? null,
    p_title: input.title ?? null,
    p_subtitle: input.subtitle ?? null,
    p_description: input.description ?? null,
    p_dates_text: input.dates_text ?? null,
    p_cover_url: input.cover_url ?? null,
    p_extra_images: input.extra_images ?? null,
    p_venue_url: input.venue_url ?? null,
    p_venue_label: input.venue_label ?? null,
    p_league_id: leagueId ?? null,
  });
  if (error) throw handleSupabaseError(error);
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) throw new Error('No se pudo actualizar el evento de finales');
  return row as AppSettings;
};
